import React, {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { htmlToSlate, slateToHtml } from '@slate-serializers/html';
import cn from 'classnames';
import DOMPurify from 'dompurify';
import isHotkey from 'is-hotkey';
import { debounce } from 'lodash';
import { Text } from 'scouting-ui/lib';
import { type Descendant, Editor, Transforms, createEditor } from 'slate';
import { withHistory } from 'slate-history';
import { Slate, withReact } from 'slate-react';

import EditorText from './EditorTextArea';
import {
  HOTKEYS,
  HTML_TO_SLATE_CONFIG,
  INITIAL_VALUE,
  SANITIZE_OPTIONS,
} from './EditorTextArea.config';
import styles from './EditorTextArea.less';
import {
  containsSpecialCharacters,
  escapeSpecialCharacters,
  toggleIndent,
  toggleMark,
  withInlines,
} from './EditorTextArea.utils';

export interface EditorTextAreaProps {
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  toolbar?: { options?: string[] };
  spellCheck?: boolean;
  value?: string;
  defaultValue?: string;
  charCounter?: boolean;
  charCounterBelow?: boolean;
  maxLength?: number;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  noBorder?: boolean;
  className?: string;
}

const EditorTextArea = ({
  charCounter,
  charCounterBelow,
  className = '',
  defaultValue,
  disabled,
  id,
  maxLength,
  noBorder,
  onBlur,
  onChange,
  placeholder,
  spellCheck,
  toolbar,
  value,
}: EditorTextAreaProps) => {
  const [valueInitialized, setValueInitialized] = useState(false);
  const [editor] = useState(() =>
    withReact(withInlines(withHistory(createEditor()))),
  );
  const charCount = (value ?? '').length;

  const debouncedOnChange = useMemo(
    () =>
      debounce(value => {
        if (onChange) {
          onChange(slateToHtml(value));
        }
      }, 250),
    [onChange],
  );

  const handleOnBlur = useCallback(() => {
    if (onBlur) {
      onBlur(slateToHtml(editor.children));
    }
  }, [onBlur, editor.children]);

  const handleOnValueChange = useCallback(
    value => debouncedOnChange(value),
    [debouncedOnChange],
  );

  const handleOnKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      // Handle Tab for paragraph indentation
      if (event.key === 'Tab') {
        event.preventDefault();
        toggleIndent(editor, event.shiftKey ? 'outdent' : 'indent');
        return;
      }

      for (const hotkey in HOTKEYS) {
        if (isHotkey(hotkey, event.nativeEvent)) {
          event.preventDefault();
          const mark = HOTKEYS[hotkey];
          toggleMark(editor, mark);
        }
      }
    },
    [editor],
  );

  const handleOnBeforeInput = useCallback((event: InputEvent) => {
    if (containsSpecialCharacters(event?.data || '')) {
      event.preventDefault();
    }
  }, []);

  const handleOnPaste = useCallback(
    (event: React.ClipboardEvent) => {
      event.preventDefault();

      const clipboardData = event.clipboardData;
      const html = clipboardData.getData('text/html');
      const text = clipboardData.getData('text/plain');

      if (html) {
        try {
          const sanitizedHtml = DOMPurify.sanitize(html, SANITIZE_OPTIONS);
          const escapedText = escapeSpecialCharacters(sanitizedHtml);

          const fragment = htmlToSlate(escapedText, HTML_TO_SLATE_CONFIG);

          if (fragment && Array.isArray(fragment)) {
            Transforms.insertFragment(editor, fragment as Descendant[]);
            return;
          }
        } catch (error: unknown) {
          return error;
        }
      }

      if (text) {
        const normalizedText = text.replace(/\r\n?/g, '\n');
        const escapedText = escapeSpecialCharacters(normalizedText);
        const fallbackHtml = escapedText
          .split('\n')
          .map(line => `<p>${line || '<br />'}</p>`)
          .join('');

        const fragment = htmlToSlate(fallbackHtml, HTML_TO_SLATE_CONFIG);

        if (fragment && Array.isArray(fragment) && fragment.length > 0) {
          Transforms.insertFragment(editor, fragment as Descendant[]);
        }
      }
    },
    [editor],
  );

  // Set default value on mount
  useEffect(() => {
    if (defaultValue) {
      editor.children = htmlToSlate(
        defaultValue || '',
        HTML_TO_SLATE_CONFIG,
      ) as Descendant[];
      editor.selection = null;
      Editor.normalize(editor, { force: true });
      editor.onChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set value on mount
  useEffect(() => {
    if (!valueInitialized && value) {
      editor.children = htmlToSlate(
        value || '',
        HTML_TO_SLATE_CONFIG,
      ) as Descendant[];
      editor.selection = null;
      Editor.normalize(editor, { force: true });
      setValueInitialized(true);
      editor.onChange();
    }

    if (valueInitialized && value == '') {
      const point = { path: [0, 0], offset: 0 };
      editor.selection = { anchor: point, focus: point };
      editor.history = { redos: [], undos: [] };
      editor.children = INITIAL_VALUE as Descendant[];
      editor.onChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, valueInitialized]);

  return (
    <div className={styles.container}>
      {charCounter && !charCounterBelow && (
        <Text font="body-body-4" className={styles.charCount}>
          {charCount}/{maxLength}
        </Text>
      )}
      <div
        className={cn(styles.editor, {
          [styles.disabled]: disabled,
          [styles.noBorder]: noBorder,
          [className]: className,
        })}
      >
        <Slate
          editor={editor}
          initialValue={INITIAL_VALUE as Descendant[]}
          onValueChange={handleOnValueChange}
        >
          <EditorText
            disabled={disabled}
            onBlur={handleOnBlur}
            onDOMBeforeInput={handleOnBeforeInput}
            onKeyDown={handleOnKeyDown}
            onPaste={handleOnPaste}
            placeholder={placeholder}
            spellCheck={spellCheck}
            toolbar={toolbar}
          />
        </Slate>
      </div>
      {charCounterBelow && (
        <>
          <label htmlFor={id}>{placeholder}</label>
          <Text font="body-body-4" className={styles.charCountBelow}>
            {charCount}/{maxLength}
          </Text>
        </>
      )}
    </div>
  );
};

export default EditorTextArea;
export { EditorTextArea };
